/** Wording for the OMS dispatch location (spec 2026-09-01 §D). Pure — no React,
 *  no Leaflet — so the copy line and badge text unit-test directly. */

import type { DispatchLocation, DispatchTarget } from '../../types/oms.ts'

const NO_ADDRESS = 'No address on file'

/** "at <addr>" for the device's own address, "near <addr> (~N m away)" for the
 *  nearest-transformer proxy, else a plain no-address line. */
export function addressLine(loc: DispatchLocation | null | undefined): string {
  if (!loc || !loc.address) return NO_ADDRESS
  if (loc.address_basis === 'nearest_transformer') {
    const d = loc.address_distance_m != null ? ` (~${Math.round(loc.address_distance_m)} m away)` : ''
    return `near ${loc.address}${d}`
  }
  return `at ${loc.address}`
}

export function targetLabel(target: DispatchTarget): string {
  switch (target) {
    case 'confirmed_device':
      return 'Confirmed source'
    case 'suggested_device':
      return 'Probable source'
    default:
      return 'Outage device'
  }
}

/** Compact list-cell badge. */
export function basisBadge(
  loc: DispatchLocation | null | undefined,
): { text: string; title: string } {
  if (!loc) return { text: '—', title: 'No dispatch location' }
  if (loc.address && loc.address_basis === 'device') return { text: '@ addr', title: loc.address }
  if (loc.address && loc.address_basis === 'nearest_transformer') {
    return { text: '≈ addr', title: addressLine(loc) }
  }
  if (loc.pole_id) return { text: 'pole only', title: `Pole ${loc.pole_id}` }
  return { text: '—', title: 'No dispatch address' }
}

export function formatLatLon(loc: DispatchLocation): string {
  return `${loc.lat.toFixed(5)}, ${loc.lon.toFixed(5)}`
}

/** One line for the clipboard / radio: label, address, pole, coordinates. */
export function dispatchCopyLine(loc: DispatchLocation): string {
  const parts: string[] = []
  if (loc.device_label) {
    parts.push(loc.device_type ? `${loc.device_label} (${loc.device_type})` : loc.device_label)
  }
  parts.push(addressLine(loc))
  if (loc.pole_id) parts.push(`pole ${loc.pole_id}`)
  parts.push(formatLatLon(loc))
  return parts.join(' · ')
}

/** Coordinates only — a nearest-transformer proxy must never route a crew to
 *  the neighbour's house. */
export function googleMapsUrl(loc: DispatchLocation): string {
  return `https://www.google.com/maps/search/?api=1&query=${loc.lat.toFixed(6)},${loc.lon.toFixed(6)}`
}
